import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";

const fourCs = [
  {
    letter: "C",
    title: "Carat",
    subtitle: "Weight & Size",
    desc: "Carat measures a diamond's weight, not its size. One carat equals 200 milligrams. While larger carat weights are rarer and more valuable, the cut quality dramatically affects how large a diamond appears to the eye. A well-cut 1-carat diamond can appear larger than a poorly cut 1.2-carat stone.",
    tip: "Focus on visual size (mm dimensions) rather than carat weight alone.",
    img: "https://readdy.ai/api/search-image?query=three%20loose%20round%20brilliant%20diamonds%20of%20different%20sizes%20arranged%20on%20dark%20black%20velvet%2C%20small%20medium%20and%20large%20carat%20weights%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%20showing%20scale%20comparison%2C%20high%20contrast%2C%20professional%20studio&width=400&height=320&seq=edu-carat&orientation=landscape",
  },
  {
    letter: "C",
    title: "Cut",
    subtitle: "Brilliance & Fire",
    desc: "Cut is the only C determined by human craftsmanship, not nature. It dictates how light enters, reflects, and exits the diamond — creating brilliance (white light), fire (colored light), and scintillation (sparkle). Excellent cut grades maximize a diamond's optical performance regardless of other specs.",
    tip: "Cut is the most important C for beauty. Never compromise on cut grade.",
    img: "https://readdy.ai/api/search-image?query=close%20up%20of%20round%20brilliant%20diamond%20showing%20perfect%20symmetrical%20facets%20and%20light%20refraction%2C%20dramatic%20dark%20background%2C%20luxury%20jewelry%20macro%20photography%2C%20extreme%20detail%20of%20pavilion%20and%20crown%20facets%2C%20sparkling%20brilliance&width=400&height=320&seq=edu-cut&orientation=landscape",
  },
  {
    letter: "C",
    title: "Color",
    subtitle: "Absence of Hue",
    desc: "Diamond color grades range from D (completely colorless) to Z (light yellow or brown). Colorless diamonds allow more light to pass through, creating greater brilliance. For lab-grown diamonds, D-F grades are more accessible than in mined diamonds, making near-colorless stones an exceptional value.",
    tip: "G-H grades appear colorless to the naked eye and offer outstanding value.",
    img: "https://readdy.ai/api/search-image?query=row%20of%20five%20round%20diamonds%20showing%20color%20grades%20from%20D%20to%20J%20on%20dark%20black%20background%2C%20subtle%20color%20progression%20from%20colorless%20to%20slight%20warmth%2C%20luxury%20jewelry%20comparison%20photography%2C%20professional%20studio%20lighting%2C%20high%20contrast&width=400&height=320&seq=edu-color&orientation=landscape",
  },
  {
    letter: "C",
    title: "Clarity",
    subtitle: "Purity & Inclusions",
    desc: "Clarity assesses the presence of internal characteristics (inclusions) and surface blemishes. Grades range from Flawless (FL) to Included (I). Most inclusions are microscopic and invisible without magnification. VS1-VS2 clarity offers an ideal balance — eye-clean beauty without the premium of higher grades.",
    tip: "VS1 and VS2 diamonds are eye-clean in virtually all cases and sizes.",
    img: "https://readdy.ai/api/search-image?query=loose%20round%20brilliant%20diamond%20on%20dark%20background%20with%20light%20passing%20through%20showing%20internal%20clarity%20and%20purity%2C%20magnified%20view%20of%20pristine%20facets%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%2C%20professional%20studio%2C%20ultra%20detailed&width=400&height=320&seq=edu-clarity&orientation=landscape",
  },
];

const shapes = [
  {
    name: "Round Brilliant",
    desc: "The classic. 57-58 facets engineered for maximum brilliance. Accounts for over 60% of all diamonds sold.",
    img: "https://readdy.ai/api/search-image?query=single%20round%20brilliant%20cut%20loose%20diamond%20on%20dark%20black%20velvet%2C%20perfectly%20symmetrical%2C%2057%20facets%20sparkling%20with%20fire%20and%20brilliance%2C%20luxury%20jewelry%20photography%2C%20dramatic%20studio%20lighting%2C%20ultra%20high%20detail&width=280&height=280&seq=edu-shape-round&orientation=squarish",
  },
  {
    name: "Princess",
    desc: "Square or rectangular with pointed corners. A brilliant cut with exceptional fire in a modern, geometric silhouette.",
    img: "https://readdy.ai/api/search-image?query=single%20princess%20cut%20loose%20diamond%20on%20dark%20black%20velvet%2C%20square%20shape%20with%20sharp%20pointed%20corners%2C%20brilliant%20facets%20creating%20intense%20sparkle%2C%20luxury%20jewelry%20photography%2C%20dramatic%20studio%20lighting%2C%20ultra%20high%20detail&width=280&height=280&seq=edu-shape-princess&orientation=squarish",
  },
  {
    name: "Cushion",
    desc: "Square with rounded corners, combining the romance of an antique cut with modern brilliant faceting for soft, pillowy sparkle.",
    img: "https://readdy.ai/api/search-image?query=single%20cushion%20cut%20loose%20diamond%20on%20dark%20black%20velvet%2C%20square%20with%20softly%20rounded%20corners%2C%20vintage%20inspired%20brilliant%20facets%2C%20warm%20romantic%20sparkle%2C%20luxury%20jewelry%20photography%2C%20dramatic%20studio%20lighting&width=280&height=280&seq=edu-shape-cushion&orientation=squarish",
  },
  {
    name: "Emerald",
    desc: "Rectangular with step-cut facets creating a hall-of-mirrors effect. Celebrated for its elegant clarity and Art Deco sophistication.",
    img: "https://readdy.ai/api/search-image?query=single%20emerald%20cut%20loose%20diamond%20on%20dark%20black%20velvet%2C%20rectangular%20with%20clipped%20corners%2C%20step%20cut%20parallel%20facets%20creating%20hall%20of%20mirrors%20effect%2C%20elegant%20and%20refined%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting&width=280&height=280&seq=edu-shape-emerald&orientation=squarish",
  },
  {
    name: "Oval",
    desc: "Elongated brilliant cut that appears larger than a round of equal carat weight. Creates a flattering, elongating effect on the finger.",
    img: "https://readdy.ai/api/search-image?query=single%20oval%20cut%20loose%20diamond%20on%20dark%20black%20velvet%2C%20elongated%20brilliant%20cut%2C%20elegant%20elongated%20shape%20with%20rounded%20ends%2C%20sparkling%20facets%2C%20luxury%20jewelry%20photography%2C%20dramatic%20studio%20lighting%2C%20ultra%20detailed&width=280&height=280&seq=edu-shape-oval&orientation=squarish",
  },
  {
    name: "Radiant",
    desc: "Rectangular with clipped corners, combining the elegance of an emerald silhouette with the fiery brilliance of a round cut.",
    img: "https://readdy.ai/api/search-image?query=single%20radiant%20cut%20loose%20diamond%20on%20dark%20black%20velvet%2C%20rectangular%20with%20clipped%20corners%2C%20brilliant%20cut%20facets%20creating%20intense%20fire%20and%20sparkle%2C%20luxury%20jewelry%20photography%2C%20dramatic%20studio%20lighting%2C%20ultra%20high%20detail&width=280&height=280&seq=edu-shape-radiant&orientation=squarish",
  },
];

const settings = [
  {
    name: "Solitaire",
    desc: "A single diamond held by minimal metal prongs. Timeless, elegant, and designed to showcase the stone's full brilliance.",
    img: "https://readdy.ai/api/search-image?query=solitaire%20diamond%20engagement%20ring%2C%20single%20round%20brilliant%20diamond%20in%20four%20gold%20prongs%2C%20minimal%20elegant%20design%2C%20dark%20black%20background%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%2C%20ultra%20detailed&width=320&height=240&seq=edu-set-solitaire&orientation=landscape",
  },
  {
    name: "Halo",
    desc: "A center stone surrounded by a ring of smaller diamonds. Amplifies sparkle and makes the center diamond appear significantly larger.",
    img: "https://readdy.ai/api/search-image?query=halo%20diamond%20engagement%20ring%2C%20center%20round%20diamond%20surrounded%20by%20circle%20of%20smaller%20diamonds%20in%20gold%20setting%2C%20dark%20black%20background%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%2C%20ultra%20detailed&width=320&height=240&seq=edu-set-halo&orientation=landscape",
  },
  {
    name: "Pavé",
    desc: "The band is encrusted with tiny diamonds set closely together, creating a continuous river of sparkle along the ring shank.",
    img: "https://readdy.ai/api/search-image?query=pave%20diamond%20engagement%20ring%2C%20band%20covered%20with%20tiny%20micro%20set%20diamonds%2C%20center%20stone%20with%20sparkling%20shank%2C%20dark%20black%20background%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%2C%20ultra%20detailed&width=320&height=240&seq=edu-set-pave&orientation=landscape",
  },
  {
    name: "Three-Stone",
    desc: "A central diamond flanked by two side stones, symbolizing past, present, and future. Rich in meaning and visual impact.",
    img: "https://readdy.ai/api/search-image?query=three%20stone%20diamond%20engagement%20ring%2C%20center%20large%20diamond%20with%20two%20smaller%20side%20diamonds%20in%20gold%20setting%2C%20symbolic%20elegant%20design%2C%20dark%20black%20background%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting&width=320&height=240&seq=edu-set-threestone&orientation=landscape",
  },
];

const compareRows = [
  { feature: "Composition", lab: "Pure carbon crystal lattice", natural: "Pure carbon crystal lattice" },
  { feature: "Hardness", lab: "10 on Mohs scale", natural: "10 on Mohs scale" },
  { feature: "Refractive Index", lab: "2.42", natural: "2.42" },
  { feature: "Certification", lab: "IGI graded using same standards", natural: "GIA / IGI graded" },
  { feature: "Traceability", lab: "Fully traceable, documented origin", natural: "Often opaque supply chain" },
  { feature: "Environmental Impact", lab: "Significantly lower footprint", natural: "Mining displaces massive earth" },
  { feature: "Price for Equal Quality", lab: "30-50% less", natural: "Premium pricing" },
];

export default function DiamondEducation() {
  const heroRef = useScrollReveal<HTMLElement>();
  const fourCsRef = useScrollReveal<HTMLElement>();
  const shapesRef = useScrollReveal<HTMLElement>();
  const compareRef = useScrollReveal<HTMLElement>();
  const certRef = useScrollReveal<HTMLElement>();
  const settingsRef = useScrollReveal<HTMLElement>();
  const ctaRef = useScrollReveal<HTMLElement>();

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="Diamond Education - Zayan Atelier" />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-[130px] md:pt-[170px] pb-16 md:pb-24 overflow-hidden reveal" ref={heroRef}>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=multiple%20loose%20brilliant%20cut%20diamonds%20scattered%20on%20dark%20black%20velvet%20surface%2C%20luxury%20jewelry%20macro%20photography%2C%20dramatic%20side%20lighting%20creating%20sparkling%20facets%2C%20high%20contrast%2C%20professional%20studio%20shot%2C%20ultra%20detailed%20gemstones&width=1400&height=700&seq=edu-hero&orientation=landscape"
            alt="Diamond education"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zayan-bg via-zayan-bg/85 to-zayan-bg" />
        </div>
        <div className="relative z-10 px-6 lg:px-16 max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-12 bg-zayan-gold/50" />
            <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase font-sans">
              The Knowledge Center
            </p>
            <div className="h-px w-12 bg-zayan-gold/50" />
          </div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-zayan-gold leading-tight mb-6">
            Diamond Education
          </h1>
          <p className="text-zayan-textMuted text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
            Understanding diamonds empowers confident decisions. Explore the fundamentals of cut, color, clarity, and carat — along with shapes, settings, and certification — curated by the specialists at Zayan Atelier.
          </p>
        </div>
      </section>

      {/* The 4Cs Section */}
      <section className="border-t border-zayan-border py-16 md:py-20 lg:py-24 reveal" ref={fourCsRef}>
        <div className="px-6 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
              The Foundation
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-zayan-gold leading-tight mb-4">
              The Four Cs
            </h2>
            <p className="text-zayan-textMuted text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              Every diamond is evaluated across four universal dimensions. Understanding them is the first step toward selecting a stone that matches your vision and values.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {fourCs.map((c, i) => (
              <div
                key={i}
                className="border border-zayan-border group hover:border-zayan-gold/30 transition-colors overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-[200px] md:w-[240px] shrink-0 h-48 sm:h-auto">
                    <img
                      src={c.img}
                      alt={c.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-serif text-4xl text-zayan-gold/30 leading-none">
                        {c.letter}
                      </span>
                      <div>
                        <h3 className="text-zayan-gold text-sm md:text-base font-medium tracking-wide">
                          {c.title}
                        </h3>
                        <p className="text-zayan-textDarker text-[11px] tracking-wider uppercase">
                          {c.subtitle}
                        </p>
                      </div>
                    </div>
                    <p className="text-zayan-textMuted text-sm leading-relaxed mb-4">
                      {c.desc}
                    </p>
                    <div className="flex items-start gap-2 text-zayan-gold/70 text-xs">
                      <i className="ri-lightbulb-line mt-0.5 shrink-0" />
                      <span>{c.tip}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diamond Shapes Section */}
      <section className="bg-zayan-bgLight border-t border-zayan-border py-16 md:py-20 lg:py-24 reveal" ref={shapesRef}>
        <div className="px-6 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
              Silhouettes
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-zayan-gold leading-tight mb-4">
              Diamond Shapes
            </h2>
            <p className="text-zayan-textMuted text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              The shape defines a diamond's personality. From the timeless round to the architectural emerald, each silhouette carries its own character, history, and optical behavior.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {shapes.map((s, i) => (
              <div
                key={i}
                className="border border-zayan-border group hover:border-zayan-gold/30 transition-colors text-center"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="text-zayan-gold text-sm font-medium tracking-wide mb-2">
                    {s.name}
                  </h3>
                  <p className="text-zayan-textMuted text-xs leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/collections"
              className="inline-flex items-center gap-2 border border-zayan-gold/50 text-zayan-gold px-6 py-3 text-xs tracking-[0.12em] hover:bg-zayan-gold hover:text-zayan-bg transition-colors whitespace-nowrap"
            >
              EXPLORE OUR COLLECTIONS
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Lab vs Natural Comparison */}
      <section className="border-t border-zayan-border py-16 md:py-20 lg:py-24 reveal" ref={compareRef}>
        <div className="px-6 lg:px-16 max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
              Transparency
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-zayan-gold leading-tight mb-4">
              Lab-Grown vs. Natural
            </h2>
            <p className="text-zayan-textMuted text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              Lab-grown diamonds are not simulants or substitutes. They are real diamonds — identical in every measurable way. The only difference is origin.
            </p>
          </div>

          <div className="border border-zayan-border overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 border-b border-zayan-border bg-zayan-bgCard">
              <div className="px-4 py-3 md:px-6 md:py-4 text-zayan-textDarker text-xs tracking-[0.1em] uppercase font-sans">
                Property
              </div>
              <div className="px-4 py-3 md:px-6 md:py-4 text-zayan-gold text-xs tracking-[0.1em] uppercase font-sans text-center border-l border-zayan-border">
                Lab-Grown
              </div>
              <div className="px-4 py-3 md:px-6 md:py-4 text-zayan-textMuted text-xs tracking-[0.1em] uppercase font-sans text-center border-l border-zayan-border">
                Natural
              </div>
            </div>
            {/* Table Rows */}
            {compareRows.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-3 ${i < compareRows.length - 1 ? "border-b border-zayan-border" : ""}`}
              >
                <div className="px-4 py-3 md:px-6 md:py-4 text-zayan-text text-xs md:text-sm font-medium">
                  {row.feature}
                </div>
                <div className="px-4 py-3 md:px-6 md:py-4 text-zayan-gold text-xs md:text-sm text-center border-l border-zayan-border">
                  {row.lab}
                </div>
                <div className="px-4 py-3 md:px-6 md:py-4 text-zayan-textMuted text-xs md:text-sm text-center border-l border-zayan-border">
                  {row.natural}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-start gap-3 text-zayan-textMuted text-sm max-w-2xl mx-auto">
            <i className="ri-information-line text-zayan-gold text-lg shrink-0 mt-0.5" />
            <p>
              <span className="text-zayan-gold">Zayan Atelier</span> specializes exclusively in IGI-certified lab-grown diamonds. We believe modern clients deserve exceptional quality, full transparency, and remarkable value — without compromise.
            </p>
          </div>
        </div>
      </section>

      {/* Certification Section */}
      <section className="bg-zayan-bgLight border-t border-zayan-border py-16 md:py-20 lg:py-24 reveal" ref={certRef}>
        <div className="px-6 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
                Assurance
              </p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-zayan-gold leading-tight mb-6">
                IGI Certification
              </h2>
              <div className="h-px w-16 bg-zayan-gold mb-6" />
              <div className="space-y-4 text-zayan-textMuted text-sm leading-relaxed">
                <p>
                  The <span className="text-zayan-text">International Gemological Institute (IGI)</span> is one of the world's largest and most respected independent gemological laboratories. Every diamond sourced through Zayan Atelier is accompanied by an IGI certificate.
                </p>
                <p>
                  Your certificate is a comprehensive document that records the diamond's exact measurements, proportions, polish, symmetry, fluorescence, and of course — the Four Cs. It serves as an immutable reference for insurance, resale, and personal records.
                </p>
                <p>
                  IGI grades lab-grown diamonds using the same rigorous standards applied to natural diamonds. There is no separate, lesser standard. A VS1 lab diamond is held to the identical clarity criteria as a VS1 mined diamond.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-zayan-textDarker text-xs tracking-[0.15em]">
                <span className="flex items-center gap-1.5">
                  <i className="ri-check-line text-zayan-gold" />
                  INDEPENDENT
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-check-line text-zayan-gold" />
                  GLOBALLY RECOGNIZED
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-check-line text-zayan-gold" />
                  LIFETIME VALID
                </span>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=diamond%20grading%20certificate%20document%20next%20to%20a%20loose%20brilliant%20cut%20diamond%20and%20jewelers%20loupe%20on%20dark%20black%20surface%2C%20luxury%20professional%20jewelry%20inspection%20scene%2C%20dramatic%20lighting%2C%20high%20contrast%2C%20ultra%20detailed&width=600&height=500&seq=edu-cert&orientation=landscape"
                alt="IGI diamond certification"
                className="w-full h-auto object-cover object-top border border-zayan-border"
              />
              <div className="absolute -bottom-4 -left-4 bg-zayan-bg border border-zayan-border p-4 md:p-5 max-w-xs">
                <p className="text-zayan-gold text-xs tracking-wider uppercase mb-1">IGI Certificate Includes</p>
                <ul className="text-zayan-textMuted text-xs space-y-1">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-zayan-gold text-[10px]" />
                    Cut, Color, Clarity, Carat
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-zayan-gold text-[10px]" />
                    Proportions & Symmetry
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-zayan-gold text-[10px]" />
                    Polish & Fluorescence
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-zayan-gold text-[10px]" />
                    Laser Inscription Number
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Setting Styles Section */}
      <section className="border-t border-zayan-border py-16 md:py-20 lg:py-24 reveal" ref={settingsRef}>
        <div className="px-6 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
              Design
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-zayan-gold leading-tight mb-4">
              Setting Styles
            </h2>
            <p className="text-zayan-textMuted text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              The setting is the stage for your diamond. It defines the ring's character, protects the stone, and determines how light interacts with the gem. Each style tells a different story.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {settings.map((s, i) => (
              <div
                key={i}
                className="border border-zayan-border group hover:border-zayan-gold/30 transition-colors overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="text-zayan-gold text-sm font-medium tracking-wide mb-2">
                    {s.name}
                  </h3>
                  <p className="text-zayan-textMuted text-xs leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-zayan-bgCard border-t border-zayan-border py-16 md:py-20 px-6 text-center reveal" ref={ctaRef}>
        <div className="max-w-2xl mx-auto">
          <div className="w-14 h-14 flex items-center justify-center rounded-full border border-zayan-gold/50 mx-auto mb-6">
            <i className="ri-vip-diamond-line text-zayan-gold text-xl" />
          </div>
          <h2 className="font-serif text-3xl lg:text-5xl text-zayan-gold mb-4">
            Ready to Find Your Diamond?
          </h2>
          <p className="text-zayan-textMuted text-sm md:text-base leading-relaxed mb-8">
            Apply what you have learned. Our specialists are ready to source a diamond that matches your exact specifications, informed by the knowledge you now hold.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/request-a-diamond"
              className="border border-zayan-gold bg-zayan-gold/10 text-zayan-gold px-8 py-3 text-xs tracking-[0.15em] hover:bg-zayan-gold hover:text-zayan-bg transition-colors whitespace-nowrap"
            >
              REQUEST A DIAMOND
            </Link>
            <Link
              to="/contact"
              className="border border-zayan-gold/50 text-zayan-textMuted px-8 py-3 text-xs tracking-[0.15em] hover:border-zayan-gold hover:text-zayan-gold transition-colors whitespace-nowrap"
            >
              SCHEDULE CONSULTATION
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}